import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/auth'
import { useGoalsState } from '@/goals'
import { useMiscellaneousContext } from '@/miscellaneous'
import { MicrophoneIcon } from '@mote/assets/icons'
import { isMobile } from '@mote/helpers/browser'
import { getGodModeURLSuffix } from '@mote/helpers/identity'
import { Button, CloseButton, Container, Link } from '@mote/ui'
import styled from '@xstyled/styled-components'
import { Logo, Sidebar } from '../shared'
import {
  DashboardContextProvider,
  useDashboardContext
} from './DashboardContextProvider'
import { NavigationalLink } from './NavigationalLink'
import { DashboardFooter } from './dashboardFooter'
import { DashboardHeader } from './dashboardHeader'

function enrichPathsWithGodMode(path) {
  const pathnames = ['activity', 'my-loops', 'my-school', 'account']
  const startsWith = pathnames.some(name => path.startsWith(name))

  return startsWith ? `${path}${getGodModeURLSuffix()}` : path
}

export const DashboardLayout = props => {
  return (
    <DashboardContextProvider>
      <DashboardLayoutRoot {...props} />
    </DashboardContextProvider>
  )
}

export const DashboardLayoutRoot = ({
  children,
  headerOverride,
  hideFooter = false,
  noPadding = false
}) => {
  const dashboardContext = useDashboardContext()
  const location = useLocation()
  const { user } = useAuth()
  const goalsState = useGoalsState()
  const miscellaneousContext = useMiscellaneousContext()

  const NAV_LINKS = useMemo(() => {
    if (!user) {
      return []
    }

    const navLinks = []

    if (
      !goalsState.userOnboardingRequired &&
      goalsState.productOnboardingRequired &&
      !(user?.isInstitutionalAdmin && !user?.isPaid)
    ) {
      const chromeExtensionTask =
        miscellaneousContext.isExtensionInstalled || isMobile()

      const tasks = [
        chromeExtensionTask,
        !goalsState.makeFirstMoteRequired
      ].filter(completed => !completed)

      if (!isMobile()) {
        navLinks.push({
          label: 'Getting Started',
          path: '/',
          notifications: tasks.length
        })
      }
    }

    navLinks.push({ label: 'My Activity', path: 'activity' })

    if (user && !user.isMinor) {
      navLinks.push({
        label: 'My Loops',
        path: 'my-loops'
      })
    }

    const mySchoolRoutes = user.isInstitutionalAdmin
      ? [
          {
            label: 'My School',
            path: 'my-school',
            childPaths: [
              {
                label: 'Manage Users',
                path: 'my-school/manage-users'
              },
              {
                label: 'Admin Insights',
                path: 'my-school/admin-insights'
              }
            ]
          }
        ]
      : []

    return [
      ...navLinks,
      {
        label: 'Account Settings',
        path: 'account'
      },
      ...mySchoolRoutes,
      {
        label: 'Pricing',
        path: 'pricing'
      },
      {
        label: 'Learning Zone',
        path: '/learning-zone',
        isExternal: true
      }
    ].map(link => {
      if (link.childPaths) {
        link.childPaths = link.childPaths.map(childLink => ({
          ...childLink,
          path: enrichPathsWithGodMode(childLink.path)
        }))
      }

      return {
        ...link,
        path: enrichPathsWithGodMode(link.path)
      }
    })
  }, [user, goalsState])

  useEffect(() => {
    if (
      dashboardContext.getShouldSidebarCollapse() &&
      dashboardContext.showSidebar
    ) {
      dashboardContext.toggleSidebar()
    }
  }, [location.pathname])

  return (
    <Root>
      <Sidebar
        isOpen={dashboardContext.showSidebar}
        onToggle={dashboardContext.toggleSidebar}
        width={sideBarWidth}
      >
        <Sidebar.Header
          layout='horizontal'
          align='center'
          justify='space-between'
          spreadX
        >
          <Logo to='/' aria-label='Mote, back to homepage' />
          {dashboardContext.getShouldSidebarCollapse() && (
            <CloseButton
              aria-label='Close sidebar'
              onClick={dashboardContext.toggleSidebar}
            />
          )}
        </Sidebar.Header>
        <Sidebar.Content layout='vertical' spreadX spreadY>
          <StyledRecordButtonWrapper>
            <StyledRecordButton />
          </StyledRecordButtonWrapper>
          {NAV_LINKS.map(link => (
            <NavigationalLink
              key={link.path}
              label={link.label}
              path={link.path}
              badgeText={link.notifications}
              activeWhen={link.activeWhen}
              childPaths={link.childPaths}
              isExternal={link.isExternal}
            />
          ))}
        </Sidebar.Content>
      </Sidebar>
      <ContentContainer noPadding={noPadding}>
        {headerOverride || <StyledHeader />}
        <StyledContent variant='wide' px={noPadding ? 0 : 4}>
          {children}
        </StyledContent>
        {hideFooter ? null : <DashboardFooter />}
      </ContentContainer>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
`

const sideBarWidth = 256
const padding = 40

const ContentContainer = styled.div(
  {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    gridTemplateColumns: '100%',
    width: '100%',
    minHeight: '100%',
    paddingLeft: `${sideBarWidth}px`,
    transition: 'padding 150ms ease-in-out',
    '@media (max-width: 920px)': {
      padding: 0
    }
  },
  props =>
    !props.noPadding && {
      padding: `0 ${padding}px`,
      paddingLeft: `${sideBarWidth + padding}px`,
      '@media (max-width: 920px)': {
        padding: '0 16px'
      }
    }
)

const StyledHeader = styled(DashboardHeader)``

const StyledContent = styled(Container)`
  display: flex;
  flex-direction: column;
`

export const StyledRecordButtonWrapper = styled.div({
  marginBottom: 8,
  display: 'flex',
  width: '100%'
})

const RecordButton = props => (
  <Button
    as={Link}
    block
    to={'/record'}
    icon={<MicrophoneIcon />}
    text={'Record'}
    {...props}
  />
)

const StyledRecordButton = styled(RecordButton)({
  'span.button-icon': {
    height: '1rem',
    width: '1rem',
    svg: {
      lineHeight: '1rem'
    }
  }
})
